import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("Thank you! We will get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      <section className="py-20" style={{backgroundColor: '#2C3540'}} data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-6" data-testid="contact-title">
              Get in <span style={{color: "#F5A623"}}>Touch</span>
            </h1>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              Let us discuss your financial goals and how we can help you achieve them.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="glass-effect p-8 md:p-12 rounded-2xl" data-testid="contact-form">
              <h2 className="font-heading text-3xl font-bold text-text-primary mb-8">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-text-primary mb-2">Your Name</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-text-primary mb-2">Email Address</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-text-primary mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="+91 98765 43210"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-text-primary mb-2">Your Goals or Requirements</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full"
                    placeholder="Tell us about your investment goals..."
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full text-lg py-6"
                  data-testid="contact-submit-button"
                >
                  {submitting ? "Sending..." : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Enquiry
                    </>
                  )}
                </Button>
              </form>
            </div>

            <div className="space-y-8">
              <div className="glass-effect p-8 rounded-2xl">
                <h3 className="font-heading text-2xl font-bold text-text-primary mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <Mail className="h-6 w-6 text-primary mr-4 mt-1" />
                    <div>
                      <p className="text-text-primary font-semibold">Email</p>
                      <a href="mailto:investwithaw@gmail.com" className="text-text-secondary hover:text-primary transition-colors">
                        investwithaw@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-effect p-8 rounded-2xl">
                <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Office Hours</h3>
                <p className="text-text-secondary">
                  Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                  Saturday: 10:00 AM - 2:00 PM IST<br />
                  Sunday: Closed
                </p>
              </div>

              <div className="glass-effect p-8 rounded-2xl">
                <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Why Choose Us?</h3>
                <ul className="space-y-3 text-text-secondary">
                  <li>✓  Goal-based investment planning</li>
                  <li>✓  Ethical and Client-first approach</li>
                  <li>✓  Behavioral finance guidance</li>
				          <li>✓  Regular portfolio reviews</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}